import {useEffect, useMemo, useRef, useState} from 'react';

import {Box, ButtonBase, ClickAwayListener, Dialog, DialogActions, DialogContent, DialogTitle, Button, Divider, Menu, MenuItem, Popper, Stack, Typography} from '@mui/material';

import {buildProductDetails} from '../../shared/productDetails';
import GmcSettingsDialog from '../../shared/gmc/GmcSettingsDialog';
import PublishConfirmDialog from './PublishConfirmDialog';
import {ProductDetailFieldsDialog} from './shared';
import {useSeoAnalysis} from '../../shared/seo/useSeoAnalysis';
import SeoScoreBadge from '../../shared/seo/SeoScoreBadge';
import {ListingSeoPanel} from '../../shared/seo/SeoPanel';
import {C} from '../constants';
import {useReviewAI} from '../hooks/useReviewAI';
import {
    buildPerSkuCommandInstruction,
    buildPerSkuCommandJobs,
    buildSameLevelFieldTargets,
    formatFieldSubgroupLabel,
    getFieldWorkflowStatus,
    getListingStats,
} from '../utils';
import FieldCard from './FieldCard';
import {AddFieldPanel, GroupPicker} from './Panels';

const DOWNLOAD_OPTIONS = [
    {label: 'All — XLSX', format: 'xlsx', scope: 'all'},
    {label: 'All — CSV', format: 'csv', scope: 'all'},
    {label: 'Approved — XLSX', format: 'xlsx', scope: 'approved'},
    {label: 'Approved — CSV', format: 'csv', scope: 'approved'},
];

function ExportDropdown({onDownload}) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    return (
        <>
            <ButtonBase
                ref={ref}
                onClick={() => setOpen((v) => !v)}
                sx={{
                    border: `1px solid ${C.border}`,
                    background: C.surfaceRaised,
                    color: C.ink,
                    borderRadius: '10px',
                    padding: '8px 14px',
                    fontFamily: 'inherit',
                    fontWeight: 700,
                    cursor: 'pointer',
                    fontSize: 12.5,
                }}>
                Export
            </ButtonBase>
            <Popper open={open} anchorEl={ref.current} placement="bottom-start" style={{zIndex: 1300}}
                modifiers={[{name: 'offset', options: {offset: [0, 6]}}]}>
                <ClickAwayListener onClickAway={() => setOpen(false)}>
                    <Stack sx={{
                        background: C.surface,
                        border: `1px solid ${C.border}`,
                        borderRadius: '10px',
                        boxShadow: `0 4px 16px ${C.shadow}`,
                        padding: '4px',
                        minWidth: 170,
                    }}>
                        {DOWNLOAD_OPTIONS.map((opt) => (
                            <ButtonBase
                                key={`${opt.format}-${opt.scope}`}
                                onClick={() => { onDownload(opt.format, opt.scope); setOpen(false); }}
                                sx={{
                                    justifyContent: 'flex-start',
                                    padding: '8px 12px',
                                    borderRadius: '8px',
                                    fontFamily: 'inherit',
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: C.ink,
                                    cursor: 'pointer',
                                    '&:hover': {background: `${C.teal}11`},
                                }}>
                                {opt.label}
                            </ButtonBase>
                        ))}
                    </Stack>
                </ClickAwayListener>
            </Popper>
        </>
    );
}

const getFieldTypeOrdinal = (listing, fieldId) => {
    const sourceField = listing.fields.find((field) => field.id === fieldId);
    if (!sourceField) return -1;

    return listing.fields
        .filter((field) => field.type === sourceField.type)
        .findIndex((field) => field.id === fieldId);
};

const findMatchingFieldIndex = (listing, sourceField, sourceTypeOrdinal) => {
    const exactIndex = listing.fields.findIndex((field) => field.id === sourceField.id);
    if (exactIndex >= 0) return exactIndex;

    const sameLabelAndType = listing.fields
        .map((field, index) => ({field, index}))
        .filter(
            ({field}) =>
                field.type === sourceField.type && field.label === sourceField.label,
        );
    if (sameLabelAndType.length === 1) {
        return sameLabelAndType[0].index;
    }

    const sameType = listing.fields
        .map((field, index) => ({field, index}))
        .filter(({field}) => field.type === sourceField.type);

    return sameType[sourceTypeOrdinal]?.index ?? -1;
};

const formatFieldLevelLabel = (fieldType, fieldOrdinal) => {
    if (fieldType === 'title') return 'Title';
    if (fieldType === 'description') return 'Description';
    if (fieldOrdinal < 0) return fieldType === 'faq' ? 'FAQ' : 'Bullet';
    if (fieldType === 'bullet') return `Bullet ${fieldOrdinal + 1}`;
    if (fieldType === 'faq') return `FAQ ${fieldOrdinal + 1}`;
    return `Field ${fieldOrdinal + 1}`;
};

export default function ListingDetail({
    allListings,
    apiKey,
    brandContext = '',
    brandProfile = null,
    charLimits,
    chatTarget,
    chatPanel,
    customCommands,
    getListingInputImages,
    getListingProduct,
    getListingVars,
    isMobile,
    listing,
    publishValidation = null,
    publishValidationByListingId = {},
    onAddField,
    onApproveAll,
    onDeleteField,
    onDownload,
    onRefreshFromShopify,
    onPublishToShopify,
    publishingToShopify = false,
    publishStatusLabel = '',
    gmc,
    onPushToGmc,
    pushingToGmc = false,
    gmcStatusLabel = '',
    onOpenChat,
    onNotify,
    onSelectListing,
    onSetSuperSku,
    onUpdateField,
    onUpdateFieldsBatch,
    profileSettings = null,
    productSource = '',
    productDetailFieldSettings = null,
    onSaveProductDetailFieldSettings,
    vars = [],
}) {
    const {rewriteTextPreservingVars} = useReviewAI({profileSettings, brandContext});
    const [showAddField, setShowAddField] = useState(false);
    const [enrichingAll, setEnrichingAll] = useState(false);
    const [confirmEnrich, setConfirmEnrich] = useState(false);
    const [batchCommandIntent, setBatchCommandIntent] = useState(null);
    const [batchCommandState, setBatchCommandState] = useState(null);
    const [fieldConfigOpen, setFieldConfigOpen] = useState(false);
    const [gmcDialogOpen, setGmcDialogOpen] = useState(false);
    const [publishMenuAnchor, setPublishMenuAnchor] = useState(null);
    // publishIntent gates every publish action behind a confirmation dialog.
    // null = no pending action; otherwise holds the destination, all
    // selectable listings, and the initially selected listing IDs.
    const [publishIntent, setPublishIntent] = useState(null);
    // Persists per-field variant-tabs scroll position across FieldCard
    // remounts. The FieldCard React key includes the selected variant key,
    // so clicking a tab unmounts/remounts the card with a fresh DOM —
    // without this ref the tabs container's scrollTop would reset to 0.
    const tabsScrollByFieldRef = useRef({});
    const allListingsRef = useRef(allListings);
    allListingsRef.current = allListings;
    const siblings = useMemo(
        () =>
            listing.superSku
                ? allListings.filter((item) => item.id !== listing.id && item.superSku === listing.superSku)
                : [],
        [allListings, listing.id, listing.superSku],
    );
    const refreshableListingCount = allListings.filter(
        (item) => item.sourceProductId,
    ).length;
    const stats = useMemo(
        () => getListingStats(listing, publishValidation),
        [listing, publishValidation],
    );
    const listingProduct = useMemo(
        () => getListingProduct(listing),
        [getListingProduct, listing],
    );
    const listingInputImages = useMemo(
        () => getListingInputImages(listing),
        [getListingInputImages, listing],
    );
    const listingDetails = useMemo(
        () =>
            buildProductDetails({
                ...(listingProduct || {}),
                vars: getListingVars(listing),
            }, productDetailFieldSettings),
        [getListingVars, listing, listingProduct, productDetailFieldSettings],
    );
    const listingMetaLine = useMemo(
        () =>
            [listingProduct?.vendor, listingProduct?.collection, listing.category]
                .filter(Boolean)
                .join(' · '),
        [listing.category, listingProduct],
    );
    const listingSourceLabel = productSource;
    const seoAnalysis = useSeoAnalysis(listing, vars, brandProfile);
    const fieldsWithTerms = useMemo(
        () => listing.fields.filter((field) => field.text.trim() && (field.searchTerms || []).length),
        [listing.fields],
    );
    const [selectedVariantByFieldId, setSelectedVariantByFieldId] = useState({});

    useEffect(() => {
        setSelectedVariantByFieldId({});
    }, [listing.id]);

    const fieldVariantsById = useMemo(() => {
        if (!listing.superSku) return {};

        const groupedListings = allListings.filter((item) => item.superSku === listing.superSku);
        if (groupedListings.length < 2) return {};

        return Object.fromEntries(
            listing.fields.map((sourceField) => {
                const sourceTypeOrdinal = getFieldTypeOrdinal(listing, sourceField.id);
                const variantsByKey = new Map();

                groupedListings.forEach((groupListing) => {
                    const matchingFieldIndex =
                        groupListing.id === listing.id
                            ? groupListing.fields.findIndex((field) => field.id === sourceField.id)
                            : findMatchingFieldIndex(
                                  groupListing,
                                  sourceField,
                                  sourceTypeOrdinal,
                              );

                    if (matchingFieldIndex < 0) return;

                    const variantField = groupListing.fields[matchingFieldIndex];
                    const subgroupLabel = formatFieldSubgroupLabel(variantField.subgroupMeta);
                    if (!subgroupLabel) return;

                    const subgroupKey =
                        variantField.subgroupMeta?.groupKey ||
                        `${groupListing.id}:${variantField.id}`;
                    const nextVariant = {
                        key: subgroupKey,
                        label: subgroupLabel,
                        listingId: groupListing.id,
                        field: variantField,
                        vars: getListingVars(groupListing),
                        isActive: groupListing.id === listing.id,
                    };
                    const existingVariant = variantsByKey.get(subgroupKey);

                    if (!existingVariant || nextVariant.isActive) {
                        variantsByKey.set(subgroupKey, nextVariant);
                    }
                });

                const variants = Array.from(variantsByKey.values()).sort((left, right) => {
                    if (left.isActive && !right.isActive) return -1;
                    if (!left.isActive && right.isActive) return 1;
                    return left.label.localeCompare(right.label);
                });

                return [sourceField.id, variants.length > 1 ? variants : []];
            }),
        );
    }, [allListings, getListingVars, listing]);
    const publishTargetListings = useMemo(
        () =>
            listing.superSku
                ? allListings.filter(
                      (item) => item.superSku === listing.superSku && item.sourceProductId,
                  )
                : listing.sourceProductId
                  ? [listing]
                  : [],
        [allListings, listing],
    );
    const canPublishToShopify = useMemo(
        () =>
            publishTargetListings.some(
                (item) =>
                    publishValidationByListingId?.[item.id]?.destinations?.shopify
                        ?.hasEligibleContent,
            ),
        [publishTargetListings, publishValidationByListingId],
    );
    const canPushToGmc = useMemo(
        () =>
            publishTargetListings.some(
                (item) =>
                    publishValidationByListingId?.[item.id]?.destinations?.gmc
                        ?.hasEligibleContent,
            ),
        [publishTargetListings, publishValidationByListingId],
    );
    const publishWarningDestinations = useMemo(
        () =>
            Object.values(publishValidation?.destinations || {}).filter(
                (destinationState) => destinationState.blockedApprovedFieldIds.length > 0,
            ),
        [publishValidation],
    );
    const publishWarningIssues = useMemo(
        () =>
            publishWarningDestinations
                .flatMap((destinationState) => destinationState.issues)
                .filter(
                    (issue, index, issues) =>
                        issues.findIndex((candidate) => candidate.id === issue.id) === index,
                )
                .slice(0, 4),
        [publishWarningDestinations],
    );
    const gmcRequiresSetup =
        !gmc?.status?.connected
        || !gmc?.status?.settings?.merchantId
        || !gmc?.status?.settings?.supplementalFeedId;
    const canOpenPublishMenu = Boolean(
        onPushToGmc || onPublishToShopify || canPublishToShopify || canPushToGmc,
    );

    // Gathers the set of listings that'd be published together. Mirrors
    // the logic in contentReview/index.jsx's publish handlers: a listing
    // in a group publishes every sibling in that group; otherwise it's
    // just itself.
    const collectPublishTargets = () => {
        if (listing.superSku && allListings?.length) {
            return allListings.filter(
                (candidate) =>
                    candidate.superSku === listing.superSku && candidate.sourceProductId,
            );
        }
        return [listing];
    };

    const collectPublishCandidates = () => {
        const candidates = (allListings || []).filter(
            (candidate) => candidate.sourceProductId,
        );
        return candidates.length ? candidates : [listing];
    };

    // Opens the pre-publish confirmation dialog. For GMC, short-circuits
    // to the settings dialog if the merchant isn't configured yet — the
    // confirmation can't be shown before we know where we'd be pushing.
    const requestPublish = (destination) => {
        if (!listing.sourceProductId) return;
        if (destination === 'gmc') {
            if (!onPushToGmc) return;
            const settings = gmc?.status?.settings;
            if (!gmc?.status?.connected || !settings?.merchantId || !settings?.supplementalFeedId) {
                setGmcDialogOpen(true);
                return;
            }
        } else if (destination === 'shopify') {
            if (!onPublishToShopify) return;
        }
        const initialTargets = collectPublishTargets();
        setPublishIntent({
            destination,
            listings: collectPublishCandidates(),
            initialSelectedListingIds: initialTargets.map((target) => target.id),
        });
    };

    const handlePublishIntentConfirm = (options = {}) => {
        if (!publishIntent) return;
        const {destination} = publishIntent;
        setPublishIntent(null);
        if (destination === 'gmc') {
            onPushToGmc?.(listing, options);
        } else if (destination === 'shopify') {
            onPublishToShopify?.(listing, options);
        }
    };

    const enrichAll = async () => {
        if (!fieldsWithTerms.length) return;
        setConfirmEnrich(false);

        setEnrichingAll(true);
        try {
            for (const field of fieldsWithTerms) {
                const nextText = await rewriteTextPreservingVars({
                    apiKey,
                    charLimit: charLimits[field.type] || 0,
                    fieldType: field.type,
                    inputImages: listingInputImages,
                    originalText: field.text,
                    vars,
                    instruction: `Rewrite this text naturally incorporating these search terms where they fit organically: ${(field.searchTerms || [])
                        .map((term) => `"${term}"`)
                        .join(', ')}.
Keep the existing tone and approximate length. Avoid keyword stuffing.
Return only the improved text.`,
                    maxTokens: 500,
                });
                if (!nextText || nextText === field.text) continue;
                onUpdateField(listing.id, field.id, {
                    text: nextText,
                    status: 'revised',
                    history: [...field.history, field.text].filter(Boolean),
                });
            }
        } finally {
            setEnrichingAll(false);
        }
    };

    const requestCommandBatch = (command, sourceListingId, sourceFieldId) => {
        const currentListings = allListingsRef.current;
        const targetSet = buildSameLevelFieldTargets(
            currentListings,
            sourceListingId,
            sourceFieldId,
        );
        const runnableTargets = targetSet.targets.filter(
            (target) => target.field.text.trim(),
        );

        if (!runnableTargets.length) {
            onNotify?.({
                message: `No non-empty ${formatFieldLevelLabel(
                    targetSet.fieldType,
                    targetSet.fieldOrdinal,
                ).toLowerCase()} fields found across the selected SKUs.`,
                type: 'info',
            });
            return;
        }

        setBatchCommandIntent({
            command,
            emptyCount: targetSet.targets.length - runnableTargets.length,
            fieldLabel: formatFieldLevelLabel(
                targetSet.fieldType,
                targetSet.fieldOrdinal,
            ),
            fieldType: targetSet.fieldType,
            // Run the command once per SKU even when grouped listings
            // currently share identical text. Each request receives that
            // SKU's own variables, product images, and content context.
            jobs: buildPerSkuCommandJobs(runnableTargets),
            missingCount: targetSet.missingListingIds.length,
            selectedSkuCount: currentListings.length,
            targetCount: runnableTargets.length,
            targetSkuCount: new Set(
                runnableTargets.map((target) => target.listingId),
            ).size,
        });
    };

    const runCommandBatch = async () => {
        if (!batchCommandIntent || batchCommandState) return;

        const intent = batchCommandIntent;
        const total = intent.jobs.length;
        const updates = [];
        let failedTargets = 0;
        let unchangedTargets = 0;
        let firstError = '';

        setBatchCommandIntent(null);
        setBatchCommandState({
            commandId: intent.command.id,
            commandLabel: intent.command.label,
            processed: 0,
            total,
        });

        for (let index = 0; index < intent.jobs.length; index += 1) {
            const job = intent.jobs[index];
            const target = job.representative;
            const targetVars = getListingVars(target.listing);

            try {
                const nextText = await rewriteTextPreservingVars({
                    apiKey,
                    charLimit: charLimits[intent.fieldType] || 0,
                    fieldType: intent.fieldType,
                    inputImages: getListingInputImages(target.listing),
                    originalText: job.originalText,
                    vars: targetVars,
                    instruction: buildPerSkuCommandInstruction({
                        prompt: intent.command.prompt,
                        listing: target.listing,
                        vars: targetVars,
                    }),
                    maxTokens: 450,
                });
                const normalizedText = typeof nextText === 'string' ? nextText.trim() : '';

                if (!normalizedText || normalizedText === job.originalText) {
                    unchangedTargets += job.targets.length;
                } else {
                    job.targets.forEach((jobTarget) => {
                        updates.push({
                            expectedText: jobTarget.field.text,
                            fieldId: jobTarget.fieldId,
                            listingId: jobTarget.listingId,
                            text: normalizedText,
                        });
                    });
                }
            } catch (batchError) {
                failedTargets += job.targets.length;
                if (!firstError) {
                    firstError = batchError.message || 'AI request failed.';
                }
            }

            setBatchCommandState((current) => ({
                ...(current || {}),
                processed: index + 1,
                total,
            }));
        }

        if (updates.length) {
            onUpdateFieldsBatch?.(updates);
        }

        const updatedCount = updates.length;
        const skippedCount =
            intent.emptyCount + intent.missingCount + unchangedTargets;
        const parts = [`Updated ${updatedCount} field(s)`];
        if (skippedCount) parts.push(`${skippedCount} skipped or unchanged`);
        if (failedTargets) parts.push(`${failedTargets} failed`);

        onNotify?.({
            message: `${parts.join(' · ')}.${firstError ? ` ${firstError}` : ''}`,
            type: failedTargets ? (updatedCount ? 'warning' : 'error') : 'success',
        });
        setBatchCommandState(null);
    };

    return (
        <Stack direction="row" sx={{height: '100%', minHeight: 0, overflow: 'hidden'}}>
            <Stack direction="column" sx={{flex: 1, minWidth: 0, minHeight: 0, overflow: 'hidden'}}>
                <Box sx={{padding: '14px 18px', borderBottom: `1px solid ${C.border}`, background: C.surface}}>
                    <Box
                        sx={{
                            background: C.surface,
                            border: `1px solid ${C.border}`,
                            borderRadius: '14px',
                            padding: '14px',
                            display: 'grid',
                            gridTemplateColumns: isMobile
                                ? '1fr'
                                : 'minmax(132px, 160px) minmax(0, 1fr)',
                            gap: '12px',
                        }}>
                        <Box
                            sx={{
                                height: isMobile ? 220 : 150,
                                borderRadius: '12px',
                                overflow: 'hidden',
                                border: `1px solid ${C.border}`,
                                background:
                                    'linear-gradient(135deg, rgba(29,158,117,.1), rgba(24,95,165,.08))',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                alignSelf: 'start',
                            }}>
                            {listingProduct?.imageUrl ? (
                                <Box
                                    component="img"
                                    src={listingProduct.imageUrl}
                                    alt={listing.name}
                                    sx={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        display: 'block',
                                    }}
                                />
                            ) : (
                                <Stack
                                    direction="column"
                                    alignItems="center"
                                    gap="8px"
                                    sx={{color: C.textMuted}}>
                                    <Box
                                        sx={{
                                            width: 54,
                                            height: 54,
                                            borderRadius: '50%',
                                            background: 'rgba(255,255,255,.12)',
                                            border: `1px solid ${C.border}`,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: 22,
                                        }}>
                                        ◌
                                    </Box>
                                    <Typography sx={{fontSize: 11.5, fontWeight: 500}}>
                                        No image available
                                    </Typography>
                                </Stack>
                            )}
                        </Box>

                        <Stack direction="column" gap="10px" sx={{minWidth: 0}}>
                            <Stack direction="row" alignItems="center" gap="6px" sx={{flexWrap: 'wrap'}}>
                                {listing.sku && (
                                    <Typography
                                        component="span"
                                        sx={{
                                            fontSize: 10,
                                            fontWeight: 700,
                                            color: C.teal,
                                            background: C.tealL,
                                            border: `1px solid ${C.teal}33`,
                                            padding: '4px 8px',
                                            borderRadius: 999,
                                            letterSpacing: '.04em',
                                            textTransform: 'uppercase',
                                        }}>
                                        {listing.sku}
                                    </Typography>
                                )}
                                {listingSourceLabel && (
                                    <Typography
                                        component="span"
                                        sx={{
                                            fontSize: 10,
                                            fontWeight: 700,
                                            color: C.textMuted,
                                            background: C.surfaceAlt,
                                            border: `1px solid ${C.border}`,
                                            padding: '4px 8px',
                                            borderRadius: 999,
                                            letterSpacing: '.04em',
                                            textTransform: 'uppercase',
                                        }}>
                                        {listingSourceLabel}
                                    </Typography>
                                )}
                            </Stack>

                            <Box>
                                <Typography sx={{fontFamily: "'Playfair Display',Georgia,serif", fontSize: 20, lineHeight: 1.15, color: C.ink}}>
                                    {listing.name}
                                </Typography>
                                {listingMetaLine && (
                                    <Typography
                                        sx={{
                                            marginTop: '4px',
                                            fontSize: 11.5,
                                            color: C.textMuted,
                                            lineHeight: 1.6,
                                        }}>
                                        {listingMetaLine}
                                    </Typography>
                                )}
                            </Box>

                            <Stack gap="8px">
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    gap="10px">
                                    <Typography
                                        sx={{
                                            fontSize: 10,
                                            fontWeight: 700,
                                            color: C.textFaint,
                                            letterSpacing: '.07em',
                                            textTransform: 'uppercase',
                                        }}>
                                        Details
                                    </Typography>
                                    {onSaveProductDetailFieldSettings && (
                                        <ButtonBase
                                            onClick={() => setFieldConfigOpen(true)}
                                            sx={{
                                                border: `1px solid ${C.border}`,
                                                background: C.surfaceRaised,
                                                color: C.textSoft,
                                                borderRadius: '8px',
                                                padding: '5px 9px',
                                                fontSize: 11,
                                                fontWeight: 700,
                                                fontFamily: 'inherit',
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    borderColor: `${C.teal}55`,
                                                    color: C.teal,
                                                    background: C.tealL,
                                                },
                                            }}>
                                            Fields
                                        </ButtonBase>
                                    )}
                                </Stack>

                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: isMobile
                                            ? '1fr'
                                            : 'repeat(auto-fit, minmax(150px, 1fr))',
                                        gap: '8px',
                                    }}>
                                    {listingDetails.length ? (
                                        listingDetails.map((detail) => (
                                            <Box
                                                key={detail.label}
                                                sx={{
                                                    padding: '8px 10px',
                                                    borderRadius: '10px',
                                                    background: C.surfaceAlt,
                                                    border: `1px solid ${C.border}`,
                                                }}>
                                                <Typography
                                                    sx={{
                                                        fontSize: 9.5,
                                                        fontWeight: 600,
                                                        color: C.textFaint,
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '.07em',
                                                        marginBottom: '3px',
                                                    }}>
                                                    {detail.label}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        fontSize: 12,
                                                        color: C.ink,
                                                        lineHeight: 1.4,
                                                        wordBreak: 'break-word',
                                                    }}>
                                                    {detail.value}
                                                </Typography>
                                            </Box>
                                        ))
                                    ) : (
                                        <Typography
                                            sx={{
                                                gridColumn: '1 / -1',
                                                fontSize: 12,
                                                color: C.textMuted,
                                                lineHeight: 1.6,
                                            }}>
                                            Product details are limited for this SKU, but variable data is still available in the editor.
                                        </Typography>
                                    )}
                                </Box>
                            </Stack>

                            <GroupPicker
                                allListings={allListings}
                                listing={listing}
                                onSetSuperSku={onSetSuperSku}
                            />
                        </Stack>
                    </Box>

                    {siblings.length > 0 && (
                        <Typography sx={{fontSize: 11.5, color: C.amber, marginTop: '8px'}}>
                            Shared template across {siblings.length + 1} variants.
                            Subgrouped fields show attribute tabs below when the copy diverges by variant.
                        </Typography>
                    )}

                    {publishWarningDestinations.length > 0 && (
                        <Box
                            sx={{
                                marginTop: '12px',
                                padding: '10px 12px',
                                borderRadius: '12px',
                                border: `1px solid ${C.amber}33`,
                                background: C.amberL,
                            }}>
                            <Typography
                                sx={{
                                    fontSize: 11,
                                    fontWeight: 800,
                                    color: C.amber,
                                    textTransform: 'uppercase',
                                    letterSpacing: '.06em',
                                    marginBottom: '6px',
                                }}>
                                Publish warning
                            </Typography>
                            <Stack direction="column" gap="4px">
                                {publishWarningDestinations.map((destinationState) => (
                                    <Typography
                                        key={destinationState.destination}
                                        sx={{fontSize: 12, color: C.ink, lineHeight: 1.45}}>
                                        <Box
                                            component="span"
                                            sx={{fontWeight: 700, color: C.amber, marginRight: '6px'}}>
                                            {destinationState.label}
                                        </Box>
                                        {destinationState.summary}
                                    </Typography>
                                ))}
                                {publishWarningIssues.map((issue) => (
                                    <Typography
                                        key={issue.id}
                                        sx={{fontSize: 11.5, color: C.textSoft, lineHeight: 1.45}}>
                                        {issue.fieldLabel}: {issue.message}
                                    </Typography>
                                ))}
                            </Stack>
                        </Box>
                    )}

                    <Stack
                        direction={isMobile ? 'column' : 'row'}
                        alignItems={isMobile ? 'stretch' : 'center'}
                        justifyContent="space-between"
                        gap="12px"
                        sx={{marginTop: '12px'}}>
                        <Stack direction="row" gap="8px" sx={{flexWrap: 'wrap'}}>
                            {batchCommandState && (
                                <Box
                                    sx={{
                                        border: `1px solid ${C.purple}33`,
                                        background: C.purpleL,
                                        color: C.purple,
                                        borderRadius: '10px',
                                        padding: '8px 12px',
                                        fontSize: 12,
                                        fontWeight: 700,
                                    }}>
                                    Running {batchCommandState.commandLabel} ·{' '}
                                    {batchCommandState.processed}/{batchCommandState.total}
                                </Box>
                            )}
                            {fieldsWithTerms.length > 0 && (
                                <ButtonBase
                                    onClick={() => setConfirmEnrich(true)}
                                    disabled={enrichingAll}
                                    sx={{
                                        border: `1px solid ${C.green}33`,
                                        background: C.greenL,
                                        color: C.green,
                                        borderRadius: '10px',
                                        padding: '8px 12px',
                                        fontFamily: 'inherit',
                                        fontWeight: 700,
                                        cursor: 'pointer',
                                    }}>
                                    {enrichingAll ? 'Enriching...' : `Enrich all (${fieldsWithTerms.length})`}
                                </ButtonBase>
                            )}
                            {onRefreshFromShopify && (
                                <ButtonBase
                                    onClick={onRefreshFromShopify}
                                    disabled={!refreshableListingCount}
                                    title="Reload every selected SKU from the loaded Shopify catalog"
                                    sx={{
                                        border: `1px solid ${C.amber}33`,
                                        background: C.amberL,
                                        color: C.amber,
                                        borderRadius: '10px',
                                        padding: '8px 12px',
                                        fontFamily: 'inherit',
                                        fontWeight: 700,
                                        cursor: refreshableListingCount ? 'pointer' : 'default',
                                        opacity: refreshableListingCount ? 1 : 0.5,
                                    }}>
                                    Refresh all ({refreshableListingCount})
                                </ButtonBase>
                            )}
                            <ButtonBase
                                onClick={onApproveAll}
                                sx={{
                                    border: `1px solid ${C.teal}33`,
                                    background: C.tealL,
                                    color: C.teal,
                                    borderRadius: '10px',
                                    padding: '8px 12px',
                                    fontFamily: 'inherit',
                                    fontWeight: 700,
                                    cursor: 'pointer',
                                }}>
                                Approve all
                            </ButtonBase>
                            <ExportDropdown onDownload={onDownload} />
                            {listing.sourceProductId && (onPublishToShopify || onPushToGmc) && (() => {
                                const isBusy = publishingToShopify || pushingToGmc;
                                const busyLabel = publishingToShopify
                                    ? publishStatusLabel || 'Publishing…'
                                    : pushingToGmc
                                        ? gmcStatusLabel || 'Pushing…'
                                        : '';
                                const mainLabel = isBusy ? busyLabel : 'Publish to Shopify';

                                return (
                                    <Stack direction="row" alignItems="center" gap="8px">
                                        <Stack direction="row" sx={{alignItems: 'stretch'}}>
                                            {onPublishToShopify && (
                                                <ButtonBase
                                                    onClick={() => requestPublish('shopify')}
                                                    disabled={isBusy || !canPublishToShopify}
                                                    sx={{
                                                        border: `1px solid ${C.blue}33`,
                                                        borderRight: 'none',
                                                        background: C.blueL,
                                                        color: C.blue,
                                                        borderRadius: '10px 0 0 10px',
                                                        padding: '8px 14px',
                                                        fontFamily: 'inherit',
                                                        fontWeight: 700,
                                                        cursor:
                                                            isBusy || !canPublishToShopify
                                                                ? 'not-allowed'
                                                                : 'pointer',
                                                        opacity:
                                                            isBusy || !canPublishToShopify
                                                                ? 0.6
                                                                : 1,
                                                    }}>
                                                    {mainLabel}
                                                </ButtonBase>
                                            )}
                                            <ButtonBase
                                                onClick={(event) => setPublishMenuAnchor(event.currentTarget)}
                                                disabled={isBusy || !canOpenPublishMenu}
                                                aria-haspopup="menu"
                                                aria-expanded={Boolean(publishMenuAnchor)}
                                                title="More publish options"
                                                sx={{
                                                    border: `1px solid ${C.blue}33`,
                                                    background: C.blueL,
                                                    color: C.blue,
                                                    borderRadius: onPublishToShopify ? '0 10px 10px 0' : '10px',
                                                    padding: '8px 8px',
                                                    minWidth: 28,
                                                    fontFamily: 'inherit',
                                                    fontWeight: 700,
                                                    fontSize: 11,
                                                    cursor:
                                                        isBusy || !canOpenPublishMenu
                                                            ? 'not-allowed'
                                                            : 'pointer',
                                                    opacity:
                                                        isBusy || !canOpenPublishMenu
                                                            ? 0.6
                                                            : 1,
                                                }}>
                                                ▾
                                            </ButtonBase>
                                        </Stack>
                                        {isBusy && Boolean(busyLabel) && (
                                            <Typography component="span" sx={{fontSize: 11, color: C.textSoft}}>
                                                batched updates
                                            </Typography>
                                        )}
                                        <Menu
                                            anchorEl={publishMenuAnchor}
                                            open={Boolean(publishMenuAnchor)}
                                            onClose={() => setPublishMenuAnchor(null)}
                                            anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                                            transformOrigin={{vertical: 'top', horizontal: 'right'}}
                                            slotProps={{
                                                paper: {
                                                    sx: {
                                                        minWidth: 240,
                                                        border: `1px solid ${C.border}`,
                                                        boxShadow: `0 6px 24px ${C.shadow}`,
                                                    },
                                                },
                                            }}>
                                            {onPublishToShopify && (
                                                <MenuItem
                                                    disabled={!canPublishToShopify}
                                                    onClick={() => {
                                                        setPublishMenuAnchor(null);
                                                        if (!canPublishToShopify) return;
                                                        requestPublish('shopify');
                                                    }}
                                                    sx={{fontSize: 13, fontWeight: 600, color: C.ink}}>
                                                    <Box sx={{display: 'flex', flexDirection: 'column', gap: '2px'}}>
                                                        <Box component="span">Publish to Shopify</Box>
                                                        <Box component="span" sx={{fontSize: 11, fontWeight: 500, color: C.textSoft}}>
                                                            Update title, description and metafields
                                                        </Box>
                                                    </Box>
                                                </MenuItem>
                                            )}
                                            {onPushToGmc && (
                                                <MenuItem
                                                    disabled={!canPushToGmc && !gmcRequiresSetup}
                                                    onClick={() => {
                                                        setPublishMenuAnchor(null);
                                                        if (!canPushToGmc && !gmcRequiresSetup) return;
                                                        requestPublish('gmc');
                                                    }}
                                                    sx={{fontSize: 13, fontWeight: 600, color: C.ink}}>
                                                    <Box sx={{display: 'flex', flexDirection: 'column', gap: '2px'}}>
                                                        <Box component="span">Push to Google Merchant</Box>
                                                        <Box component="span" sx={{fontSize: 11, fontWeight: 500, color: C.textSoft}}>
                                                            {gmc?.status?.connected
                                                                ? 'Override title & description via supplemental feed'
                                                                : 'Not connected — opens settings'}
                                                        </Box>
                                                    </Box>
                                                </MenuItem>
                                            )}
                                            {onPushToGmc && <Divider sx={{margin: '4px 0'}} />}
                                            {onPushToGmc && (
                                                <MenuItem
                                                    onClick={() => {
                                                        setPublishMenuAnchor(null);
                                                        setGmcDialogOpen(true);
                                                    }}
                                                    sx={{fontSize: 12, color: C.textSoft}}>
                                                    Google Merchant settings…
                                                </MenuItem>
                                            )}
                                        </Menu>
                                    </Stack>
                                );
                            })()}
                        </Stack>

                        <Stack direction="row" alignItems="center" gap="12px" sx={{flexWrap: 'wrap'}}>
                        {seoAnalysis && (
                            <Stack direction="row" alignItems="center" gap="5px">
                                <Typography component="span" sx={{fontSize: 10, fontWeight: 800, color: C.textSoft, textTransform: 'uppercase'}}>
                                    SEO
                                </Typography>
                                <SeoScoreBadge grade={seoAnalysis.grade} score={seoAnalysis.score} size="small" />
                            </Stack>
                        )}
                        {[
                            {label: 'Total', value: stats.total, color: C.textSoft},
                            {label: 'Approved', value: stats.approved, color: C.teal},
                            {label: 'Flagged', value: stats.flagged, color: C.coral},
                            {label: 'Pending', value: stats.pending, color: C.amber},
                        ].map((item) => (
                            <Stack key={item.label} direction="row" alignItems="center" gap="5px">
                                <Typography component="span" sx={{fontSize: 10, fontWeight: 700, color: C.textSoft, textTransform: 'uppercase'}}>
                                    {item.label}
                                </Typography>
                                <Typography component="span" sx={{fontSize: 12, fontWeight: 700, color: item.color}}>{item.value}</Typography>
                            </Stack>
                        ))}
                        <Box sx={{flex: 1, height: 4, borderRadius: 999, background: C.surfaceAlt, overflow: 'hidden', minWidth: 160}}>
                            <Box
                                sx={{
                                    width: `${stats.total ? (stats.approved / stats.total) * 100 : 0}%`,
                                    height: '100%',
                                    background: C.teal,
                                }}
                            />
                        </Box>
                    </Stack>
                    </Stack>
                </Box>

                <Box sx={{flex: 1, minHeight: 0, overflowY: 'auto', padding: '18px 22px'}}>

                    {seoAnalysis && (
                        <Box sx={{marginBottom: '14px'}}>
                            <ListingSeoPanel analysis={seoAnalysis} />
                        </Box>
                    )}

                    <Box sx={{display: 'grid', gap: '12px'}}>
                        {listing.fields.map((field) => {
                            const variants = fieldVariantsById[field.id] || [];
                            const defaultVariant =
                                variants.find((variant) => variant.isActive) || variants[0] || null;
                            const selectedVariant =
                                variants.find(
                                    (variant) =>
                                        variant.key === selectedVariantByFieldId[field.id],
                                ) || defaultVariant;
                            const renderedField = selectedVariant?.field || field;
                            const renderedListingId = selectedVariant?.listingId || listing.id;
                            const renderedListing =
                                renderedListingId === listing.id
                                    ? listing
                                    : allListings.find(
                                          (candidate) => candidate.id === renderedListingId,
                                      ) || listing;
                            const renderedInputImages =
                                getListingInputImages(renderedListing);
                            const renderedVars = selectedVariant?.vars || vars;
                            const chatOpen =
                                chatTarget?.listingId === renderedListingId &&
                                chatTarget?.fieldId === renderedField.id;
                            const fieldPublishValidation =
                                publishValidationByListingId?.[renderedListingId]?.fieldsById?.[
                                    renderedField.id
                                ] || null;

                            return (
                                <FieldCard
                                    key={`${field.id}:${selectedVariant?.key || 'base'}`}
                                    apiKey={apiKey}
                                    chatOpen={chatOpen}
                                    charLimit={charLimits[field.type] || 0}
                                    customCommands={customCommands}
                                    batchCommandState={batchCommandState}
                                    selectedSkuCount={allListings.length}
                                    field={renderedField}
                                    isGrouped={Boolean(renderedListing.superSku)}
                                    displayStatus={getFieldWorkflowStatus(
                                        renderedField,
                                        fieldPublishValidation,
                                    )}
                                    inputImages={renderedInputImages}
                                    initialEditing={false}
                                    onDelete={() =>
                                        onDeleteField(renderedListingId, renderedField.id)
                                    }
                                    onOpenChat={() =>
                                        onOpenChat(
                                            renderedListingId,
                                            chatOpen ? null : renderedField.id,
                                        )
                                    }
                                    onUpdate={(fieldId, patch) =>
                                        onUpdateField(renderedListingId, fieldId, patch)
                                    }
                                    onRunCommandAcrossSkus={(command) =>
                                        requestCommandBatch(
                                            command,
                                            renderedListingId,
                                            renderedField.id,
                                        )
                                    }
                                    brandContext={brandContext}
                                    profileSettings={profileSettings}
                                    publishIssues={
                                        fieldPublishValidation?.issues || []
                                    }
                                    seoAnalysis={
                                        field.type === 'faq'
                                            ? null
                                            : seoAnalysis?.fieldAnalyses?.get(field.id) || null
                                    }
                                    vars={renderedVars}
                                    variants={variants}
                                    selectedVariantKey={selectedVariant?.key || null}
                                    onSelectVariant={(variantKey) => {
                                        setSelectedVariantByFieldId((current) => ({
                                            ...current,
                                            [field.id]: variantKey,
                                        }));
                                        // Switch the active listing to the
                                        // one backing the picked subgroup so
                                        // the rest of the UI (sidebar, vars,
                                        // publish buttons, etc.) follows
                                        // along. Skip if it's already the
                                        // active listing or has no listingId.
                                        const picked = variants.find(
                                            (entry) => entry.key === variantKey,
                                        );
                                        if (
                                            picked?.listingId
                                            && picked.listingId !== listing.id
                                            && onSelectListing
                                        ) {
                                            onSelectListing(picked.listingId);
                                        }
                                    }}
                                    initialTabsScroll={
                                        tabsScrollByFieldRef.current[field.id] || null
                                    }
                                    onTabsScroll={(position) => {
                                        tabsScrollByFieldRef.current[field.id] = position;
                                    }}
                                />
                            );
                        })}
                    </Box>

                    {showAddField ? (
                        <Box sx={{marginTop: '12px'}}>
                            <AddFieldPanel
                                onAdd={onAddField}
                                onClose={() => setShowAddField(false)}
                            />
                        </Box>
                    ) : (
                        <ButtonBase
                            onClick={() => setShowAddField(true)}
                            sx={{
                                width: '100%',
                                marginTop: '12px',
                                border: `1px dashed ${C.border2}`,
                                background: 'transparent',
                                color: C.textSoft,
                                borderRadius: '10px',
                                padding: '9px 12px',
                                fontFamily: 'inherit',
                                fontWeight: 700,
                                cursor: 'pointer',
                            }}>
                            Add field
                        </ButtonBase>
                    )}
                </Box>
            </Stack>

            {!isMobile && chatPanel && (
                <Box sx={{width: 360, flexShrink: 0, borderLeft: `1px solid ${C.border}`, height: '100%', overflow: 'hidden'}}>{chatPanel}</Box>
            )}

            <GmcSettingsDialog
                open={gmcDialogOpen}
                onClose={() => setGmcDialogOpen(false)}
                gmc={gmc}
            />

            <PublishConfirmDialog
                open={Boolean(publishIntent)}
                destination={publishIntent?.destination || 'shopify'}
                listings={publishIntent?.listings || []}
                initialSelectedListingIds={
                    publishIntent?.initialSelectedListingIds || []
                }
                onConfirm={handlePublishIntentConfirm}
                onCancel={() => setPublishIntent(null)}
            />

            <ProductDetailFieldsDialog
                availableVars={getListingVars(listing)}
                error={productDetailFieldSettings?.error}
                fields={productDetailFieldSettings?.fields || []}
                onClose={() => setFieldConfigOpen(false)}
                onSave={onSaveProductDetailFieldSettings}
                open={fieldConfigOpen}
                saving={productDetailFieldSettings?.saving}
            />

            <Dialog open={confirmEnrich} onClose={() => setConfirmEnrich(false)} maxWidth="xs">
                <DialogTitle sx={{fontSize: 16, fontWeight: 700}}>Enrich all fields?</DialogTitle>
                <DialogContent>
                    <Typography sx={{fontSize: 13, color: C.textSoft}}>
                        This will rewrite {fieldsWithTerms.length} field(s) to incorporate search terms. Existing text will be replaced. You can undo individual changes afterward.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmEnrich(false)} color="inherit">Cancel</Button>
                    <Button onClick={enrichAll} variant="contained" color="primary">Enrich all</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={Boolean(batchCommandIntent)}
                onClose={() => setBatchCommandIntent(null)}
                maxWidth="xs"
                fullWidth>
                <DialogTitle sx={{fontSize: 16, fontWeight: 700}}>
                    Run {batchCommandIntent?.command?.label || 'command'} across selected SKUs?
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{fontSize: 13, color: C.textSoft, lineHeight: 1.5}}>
                        This will rewrite {batchCommandIntent?.targetCount || 0} matching{' '}
                        {batchCommandIntent?.fieldLabel || 'content'} field(s) across{' '}
                        {batchCommandIntent?.targetSkuCount || 0} of{' '}
                        {batchCommandIntent?.selectedSkuCount || 0} selected SKU(s), using{' '}
                        {batchCommandIntent?.jobs?.length || 0} AI request(s). Existing text will
                        be saved in each field's history.
                    </Typography>
                    {Boolean(
                        batchCommandIntent?.emptyCount || batchCommandIntent?.missingCount,
                    ) && (
                        <Typography sx={{fontSize: 12, color: C.amber, marginTop: '10px'}}>
                            {batchCommandIntent?.emptyCount || 0} empty and{' '}
                            {batchCommandIntent?.missingCount || 0} missing matching field(s) will
                            be skipped.
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBatchCommandIntent(null)} color="inherit">
                        Cancel
                    </Button>
                    <Button onClick={runCommandBatch} variant="contained" color="primary">
                        Run across SKUs
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
}
